<template>
  <div>
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="400">
      <material-card
        v-if="formula"
        :title="
          $t('components.formulas.delete_formula_{0}', [
            $t('enums.' + formula.SystemName),
          ])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="tryDelete">
            {{ $t("globals.remove") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.view" persistent max-width="800">
      <material-card
        v-if="formula"
        :title="
          $t('components.formulas.view_formula_{0}', [
            $t('enums.' + formula.SystemName),
          ])
        "
        :color="$vuetify.theme.themes.light.primary"
        icon="remove_red_eye"
      >
        <v-card-text>{{ formula.Formula }}</v-card-text>
        <v-card-actions>
          <v-btn color="primary" text @click="close">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog
      v-model="dialogs.builder"
      scrollable
      persistent
      max-width="1400"
      style="height: 90%"
    >
      <material-card
        :title="formTitle"
        :icon="formIcon"
        :color="formColor"
        v-if="formula"
      >
        <v-card-text>
          <formula-builder
            ref="builder"
            :planIdentityId="planIdentityId"
            :params="params"
            :code.sync="formula.Formula"
            @valid="valid = $event"
            @tested="tested = $event"
            :fields="fields"
            :coverage="coverage"
            :trackHours="trackHours"
            :hasStores="hasStores"
            :planVariables="planVariables"
          ></formula-builder>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close">{{ $t("globals.cancel") }}</v-btn>
          <v-btn :disabled="!valid || !tested" color="primary" @click="save">
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-expansion-panels accordion :disabled="disabled">
      <v-expansion-panel v-for="(coverageItem, i) in coverage" :key="i">
        <v-expansion-panel-header :class="'formula-group-header-' + (i % 4)">
          <v-container>
            <v-row no-gutters>
              <v-col cols="auto">
                <v-icon>policy</v-icon>
              </v-col>
              <v-col style="margin-top: 6px; margin-left: 20px">
                {{
                  trans(coverageItem, "Name") +
                  " (" +
                  trans(coverageItem, "Description") +
                  ")"
                }}
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <template v-if="coverageItem.PlanInsuranceCarrier">
              <v-row v-if="coverageItem.HasVolume" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.premium_volume") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PremiumVolume' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('PremiumVolume', coverageItem, {
                        filters: ['PremiumVolume'],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PremiumVolume' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('PremiumVolume', coverageItem, {
                            filters: ['PremiumVolume'],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PremiumVolume', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PremiumVolume",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.premium") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'Premium' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('Premium', coverageItem, {
                        filters: ['PremiumVolume', 'Premium'],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'Premium' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('Premium', coverageItem, {
                            filters: ['PremiumVolume', 'Premium'],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('Premium', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-if="coverageItem.IncludePPT" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.PPTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PPTTax',
                        coverageItem,
                        {
                          filters: ['PremiumVolume', 'Premium'],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTTax',
                            coverageItem,
                            {
                              filters: ['PremiumVolume', 'Premium'],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('PPTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludeRST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.RSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'RSTTax',
                        coverageItem,
                        {
                          filters: ['PremiumVolume', 'Premium'],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTTax',
                            coverageItem,
                            {
                              filters: ['PremiumVolume', 'Premium'],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('RSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludeHST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.HSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'HSTTax',
                        coverageItem,
                        {
                          filters: [
                            'PremiumVolume',
                            'Premium',
                            'AdminFee',
                            'Remittance',
                            'RemittanceVolume',
                          ],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTTax',
                            coverageItem,
                            {
                              filters: [
                                'PremiumVolume',
                                'Premium',
                                'AdminFee',
                                'Remittance',
                                'RemittanceVolume',
                              ],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('HSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludeGST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.GSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'GSTTax',
                        coverageItem,
                        {
                          filters: [
                            'PremiumVolume',
                            'Premium',
                            'AdminFee',
                            'Remittance',
                            'RemittanceVolume',
                          ],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTTax',
                            coverageItem,
                            {
                              filters: [
                                'PremiumVolume',
                                'Premium',
                                'AdminFee',
                                'Remittance',
                                'RemittanceVolume',
                              ],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('GSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludePST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.PSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PSTTax',
                        coverageItem,
                        {
                          filters: [
                            'PremiumVolume',
                            'Premium',
                            'AdminFee',
                            'Remittance',
                            'RemittanceVolume',
                          ],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTTax',
                            coverageItem,
                            {
                              filters: [
                                'PremiumVolume',
                                'Premium',
                                'AdminFee',
                                'Remittance',
                                'RemittanceVolume',
                              ],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('PSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="hasAdminFees && coverageItem.HasAdminFees"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'AdminFee' &&  o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('AdminFee', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'AdminFee' &&  o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('AdminFee', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('AdminFee', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "AdminFee",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminPPT"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PPTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PPTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminRST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'RSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('RSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminHST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'HSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('HSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminPST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminGST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'GSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('GSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.IncludeClaimAdminFee"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimAdminFee' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('ClaimAdminFee', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                          'ClaimAdminFee'
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimAdminFee' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('ClaimAdminFee', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                              'ClaimAdminFee'
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('ClaimAdminFee', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimAdminFee",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimPPTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimPPTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimPPTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimPPTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimPPTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimPPTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimPPTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimRSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimRSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimRSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimRSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimRSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimRSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimRSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimHSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimHSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimHSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimHSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimHSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimHSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimHSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimPSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimPSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimPSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimPSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimPSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimPSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimPSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimGSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimGSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimGSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimGSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimGSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimGSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimGSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimQSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimQSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimQSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimQSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimQSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimQSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimQSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.IncludeClaimCommission"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommission' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('ClaimCommission', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                          'ClaimCommission'
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommission' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('ClaimCommission', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                              'ClaimCommission'
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('ClaimCommission', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommission",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionPPTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionPPTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionPPTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionPPTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionPPTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionPPTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionPPTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionRSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionRSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionRSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionRSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionRSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionRSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionRSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionHSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionHSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionHSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionHSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionHSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionHSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionHSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionPSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionPSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionPSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionPSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionPSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionPSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionPSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionGSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionGSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionGSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionGSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionGSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionGSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionGSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionQSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionQSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionQSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionQSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionQSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionQSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionQSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.IncludeFlatCommission"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatCommission' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('FlatCommission', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                          'FlatCommission'
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatCommission' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('FlatCommission', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                              'FlatCommission'
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('FlatCommission', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatCommission",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatPPTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatPPTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatPPTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatPPTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatPPTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatPPTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatPPTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatRSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatRSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatRSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatRSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatRSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatRSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatRSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatHSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatHSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatHSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatHSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatHSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatHSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatHSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatPSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatPSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatPSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatPSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatPSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatPSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatPSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatGSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatGSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatGSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatGSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatGSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatGSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatGSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatQSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatQSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatQSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatQSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatQSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatQSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatQSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="hasOperatingFees && coverageItem.HasOperatingFees"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'OperatingFee' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('OperatingFee', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'OperatingFee',
                          'Remittance',
                          'RemittanceVolume',
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'OperatingFee' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('OperatingFee', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'OperatingFee',
                              'Remittance',
                              'RemittanceVolume',
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('OperatingFee', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "OperatingFee",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingPPT"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PPTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PPTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingRST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'RSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('RSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingHST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'HSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('HSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingPST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingGST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'GSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('GSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.HasCommissions"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commissions") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'Commission' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('Commission', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'Commission',
                          'Remittance',
                          'RemittanceVolume',
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'Commission' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('Commission', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'Commission',
                              'Remittance',
                              'RemittanceVolume',
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('Commission', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "Commission",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                  
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionPPT"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'PPTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PPTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionRST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'RSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('RSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionHST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'HSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('HSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionPST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'PSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionGST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'GSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('GSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

            </template>
            <template v-else-if="coverageItem.TrustFund">
              <v-row v-if="coverageItem.HasVolume" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.remittance_volume") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RemittanceVolume' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('RemittanceVolume', coverageItem, {
                        filters: ['RemittanceVolume'],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RemittanceVolume' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RemittanceVolume',
                            coverageItem,
                            {
                              filters: ['RemittanceVolume'],
                            }
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula(
                            'RemittanceVolume',
                            coverageItem
                          )
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RemittanceVolume",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.remittance") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'Remittance' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('Remittance', coverageItem, {
                        filters: ['RemittanceVolume', 'Remittance'],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'Remittance' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('Remittance', coverageItem, {
                            filters: ['Remittance', 'RemittanceVolume'],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('Remittance', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-if="coverageItem.IncludePPT" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.PPTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PPTTax',
                        coverageItem,
                        {
                          filters: ['RemittanceVolume', 'Remittance'],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTTax',
                            coverageItem,
                            {
                              filters: ['Remittance', 'RemittanceVolume'],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('PPTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludeRST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.RSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'RSTTax',
                        coverageItem,
                        {
                          filters: ['RemittanceVolume', 'Remittance'],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTTax',
                            coverageItem,
                            {
                              filters: ['Remittance', 'RemittanceVolume'],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('RSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludeHST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.HSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'HSTTax',
                        coverageItem,
                        {
                          filters: [
                            'RemittanceVolume',
                            'Remittance',
                            'AdminFee',
                            'Premium',
                            'PremiumVolume',
                          ],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTTax',
                            coverageItem,
                            {
                              filters: [
                                'PremiumVolume',
                                'Premium',
                                'AdminFee',
                                'Remittance',
                                'RemittanceVolume',
                              ],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('HSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludeGST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.GSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'GSTTax',
                        coverageItem,
                        {
                          filters: [
                            'RemittanceVolume',
                            'Remittance',
                            'AdminFee',
                            'Premium',
                            'PremiumVolume',
                          ],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTTax',
                            coverageItem,
                            {
                              filters: [
                                'PremiumVolume',
                                'Premium',
                                'AdminFee',
                                'Remittance',
                                'RemittanceVolume',
                              ],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('GSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row v-if="coverageItem.IncludePST" justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("headers.adjustments.PSTax") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PSTTax',
                        coverageItem,
                        {
                          filters: [
                            'RemittanceVolume',
                            'Remittance',
                            'AdminFee',
                            'Premium',
                            'PremiumVolume',
                          ],
                        },
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTTax',
                            coverageItem,
                            {
                              filters: [
                                'PremiumVolume',
                                'Premium',
                                'AdminFee',
                                'Remittance',
                                'RemittanceVolume',
                              ],
                            },
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('PSTTax', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="hasAdminFees && coverageItem.HasAdminFees"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'AdminFee' &&  o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('AdminFee', coverageItem, {
                        filters: [
                          'RemittanceVolume',
                          'Remittance',
                          'AdminFee',
                          'Premium',
                          'PremiumVolume',
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'AdminFee' &&  o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('AdminFee', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('AdminFee', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "AdminFee",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminPPT"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PPTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PPTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminRST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'RSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('RSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminHST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'HSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('HSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminPST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeAdminGST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTAdminTax' && o.CoverageIdentity &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'GSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTAdminTax' && o.CoverageIdentity &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('GSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.IncludeClaimAdminFee"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimAdminFee' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('ClaimAdminFee', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                          'ClaimAdminFee'
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimAdminFee' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('ClaimAdminFee', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                              'ClaimAdminFee'
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('ClaimAdminFee', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimAdminFee",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimPPTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimPPTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimPPTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimPPTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimPPTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimPPTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimPPTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimRSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimRSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimRSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimRSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimRSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimRSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimRSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimHSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimHSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimHSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimHSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimHSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimHSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimHSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimPSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimPSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimPSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimPSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimPSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimPSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimPSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimGSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimGSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimGSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimGSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimGSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimGSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimGSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimQSTAdminTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fees_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimQSTAdminTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimQSTAdminTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimQSTAdminTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimQSTAdminTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimQSTAdminTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimQSTAdminTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.IncludeClaimCommission"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommission' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('ClaimCommission', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                          'ClaimCommission'
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommission' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('ClaimCommission', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                              'ClaimCommission'
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('ClaimCommission', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommission",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionPPTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionPPTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionPPTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionPPTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionPPTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionPPTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionPPTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionRSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionRSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionRSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionRSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionRSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionRSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionRSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionHSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionHSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionHSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionHSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionHSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionHSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionHSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionPSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionPSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionPSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionPSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionPSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionPSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionPSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionGSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionGSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionGSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionGSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionGSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionGSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionGSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeClaimCommissionQSTTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_commission_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'ClaimCommissionQSTTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'ClaimCommissionQSTTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'ClaimCommissionQSTTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'ClaimCommissionQSTTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('ClaimCommissionQSTTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "ClaimCommissionQSTTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.IncludeFlatCommission"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatCommission' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('FlatCommission', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'AdminFee',
                          'Remittance',
                          'RemittanceVolume',
                          'FlatCommission'
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatCommission' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('FlatCommission', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'AdminFee',
                              'Remittance',
                              'RemittanceVolume',
                              'FlatCommission'
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('FlatCommission', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatCommission",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatPPTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatPPTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatPPTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatPPTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatPPTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatPPTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatPPTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatRSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatRSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatRSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatRSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatRSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatRSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatRSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatHSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatHSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatHSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatHSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatHSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatHSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatHSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatPSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatPSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatPSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatPSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatPSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatPSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatPSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatGSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatGSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatGSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatGSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatGSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatGSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatGSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeFlatQSTCommissionTax"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_commission_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'FlatQSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'FlatQSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'FlatQSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'FlatQSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('FlatQSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "FlatQSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="hasOperatingFees && coverageItem.HasOperatingFees"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'OperatingFee' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula('OperatingFee', coverageItem, {
                        filters: [
                          'RemittanceVolume',
                          'Remittance',
                          'OperatingFee',
                          'Premium',
                          'PremiumVolume',
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'OperatingFee' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('OperatingFee', coverageItem, {
                            filters: [
                              'RemittanceVolume',
                              'Remittance',
                              'OperatingFee',
                              'Premium',
                              'PremiumVolume',
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('OperatingFee', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "OperatingFee",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingPPT"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PPTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PPTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingRST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'RSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('RSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingHST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'HSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('HSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingPST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'PSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeOperatingGST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTOperatingTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'GSTOperatingTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTOperatingTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTOperatingTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('GSTOperatingTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTOperatingTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>

              <v-row
                v-if="coverageItem.HasCommissions"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commissions") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'Commission' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula('Commission', coverageItem, {
                        filters: [
                          'PremiumVolume',
                          'Premium',
                          'Commission',
                          'Remittance',
                          'RemittanceVolume',
                        ],
                      })
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'Commission' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula('Commission', coverageItem, {
                            filters: [
                              'PremiumVolume',
                              'Premium',
                              'Commission',
                              'Remittance',
                              'RemittanceVolume',
                            ],
                          })
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteCoverageFormula('Commission', coverageItem)"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "Commission",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                  
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionPPT"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PPTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'PPTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PPTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PPTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PPTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PPTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionRST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'RSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'RSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'RSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'RSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('RSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "RSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionHST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'HSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'HSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'HSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'HSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('HSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "HSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionPST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'PSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'PSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'PSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'PSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('PSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "PSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
              <v-row
                v-if="coverageItem.IncludeCommissionGST"
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.commission_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'GSTCommissionTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="
                      addCoverageFormula(
                        'GSTCommissionTax',
                        coverageItem,
                        null,
                        true
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'GSTCommissionTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'GSTCommissionTax',
                            coverageItem,
                            null,
                            true
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula('GSTCommissionTax', coverageItem)
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "GSTCommissionTax",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
            </template>
            <template v-if="coverageItem.IsTaxableBenefit">
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{
                    $t("components.formulas.provincial_taxable_benefit_formula")
                  }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'TaxableBenefitProvincial' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'TaxableBenefitProvincial',
                        coverageItem,
                        {
                          negation: true,
                          filters: [
                            'TaxableBenefitFederal',
                            'TaxableBenefitProvincialTax',
                            'AdminFee',
                            'TaxableBenefitFederalTax',
                          ],
                        }
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'TaxableBenefitProvincial' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'TaxableBenefitProvincial',
                            coverageItem,
                            {
                              negation: true,
                              filters: [
                                'TaxableBenefitFederal',
                                'TaxableBenefitProvincialTax',
                                'AdminFee',
                                'TaxableBenefitFederalTax',
                              ],
                            }
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula(
                            'TaxableBenefitProvincial',
                            coverageItem
                          )
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{
                    $t("components.formulas.federal_taxable_benefit_formula")
                  }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'TaxableBenefitFederal' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'TaxableBenefitFederal',
                        coverageItem,
                        {
                          negation: true,
                          filters: [
                            'TaxableBenefitProvincial',
                            'TaxableBenefitProvincialTax',
                            'AdminFee',
                            'TaxableBenefitFederalTax',
                          ],
                        }
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'TaxableBenefitFederal' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'TaxableBenefitFederal',
                            coverageItem,
                            {
                              negation: true,
                              filters: [
                                'TaxableBenefitProvincial',
                                'TaxableBenefitProvincialTax',
                                'AdminFee',
                                'TaxableBenefitFederalTax',
                              ],
                            }
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula(
                            'TaxableBenefitFederal',
                            coverageItem
                          )
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{
                    $t(
                      "components.formulas.provincial_taxable_benefit_tax_formula"
                    )
                  }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'TaxableBenefitProvincialTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'TaxableBenefitProvincialTax',
                        coverageItem,
                        {
                          negation: true,
                          filters: [
                            'TaxableBenefitFederal',
                            'AdminFee',
                            'TaxableBenefitFederalTax',
                          ],
                        }
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName ===
                                  'TaxableBenefitProvincialTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'TaxableBenefitProvincialTax',
                            coverageItem,
                            {
                              negation: true,
                              filters: [
                                'TaxableBenefitFederal',
                                'AdminFee',
                                'TaxableBenefitFederalTax',
                              ],
                            }
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula(
                            'TaxableBenefitProvincialTax',
                            coverageItem
                          )
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row
                v-if="
                  formulas.filter(
                    (o) =>
                      o.SystemName === 'TaxableBenefitFederal' &&
                      o.CoverageIdentity.Identifier === coverageItem.Identifier
                  ).length > 0
                "
                justify="space-between"
              >
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{
                    $t(
                      "components.formulas.federal_taxable_benefit_tax_formula"
                    )
                  }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter(
                      (o) =>
                        o.SystemName === 'TaxableBenefitFederalTax' &&
                        o.CoverageIdentity.Identifier ===
                          coverageItem.Identifier
                    ).length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="
                      addCoverageFormula(
                        'TaxableBenefitFederalTax',
                        coverageItem,
                        {
                          negation: true,
                          filters: [
                            'TaxableBenefitProvincial',
                            'AdminFee',
                            'TaxableBenefitProvincialTax',
                          ],
                        }
                      )
                    "
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) =>
                                o.SystemName === 'TaxableBenefitFederalTax' &&
                                o.CoverageIdentity.Identifier ===
                                  coverageItem.Identifier
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="
                          editCoverageFormula(
                            'TaxableBenefitFederalTax',
                            coverageItem,
                            {
                              negation: true,
                              filters: [
                                'TaxableBenefitProvincial',
                                'AdminFee',
                                'TaxableBenefitProvincialTax',
                              ],
                            }
                          )
                        "
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="
                          deleteCoverageFormula(
                            'TaxableBenefitFederalTax',
                            coverageItem
                          )
                        "
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-else>
                {{
                  clearFormula({
                    systemName: "TaxableBenefitFederal",
                    coverageIdentifier: coverageItem.Identifier,
                  })
                }}
              </v-row>
            </template>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "TaxableBenefitProvincial",
                  coverageIdentifier: coverageItem.Identifier,
                })
              }}
              {{
                clearFormula({
                  systemName: "TaxableBenefitFederal",
                  coverageIdentifier: coverageItem.Identifier,
                })
              }}
              {{
                clearFormula({
                  systemName: "TaxableBenefitProvincialTax",
                  coverageIdentifier: coverageItem.Identifier,
                })
              }}
              {{
                clearFormula({
                  systemName: "TaxableBenefitFederalTax",
                  coverageIdentifier: coverageItem.Identifier,
                })
              }}
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel
        v-if="
          hasContributions ||
          operatingFeesPerTerm ||
          adminFeesPerTerm ||
          hasEmployerContributionsPerTerm ||
          hasClaimAdminFees ||
          hasFlatAdminFees
        "
      >
        <v-expansion-panel-header
          :class="'formula-group-header-' + (coverage.length % 4)"
        >
          <v-container>
            <v-row no-gutters>
              <v-col cols="auto">
                <v-icon>insert_drive_file</v-icon>
              </v-col>
              <v-col style="margin-top: 6px; margin-left: 20px">
                {{ $t("globals.plan") }}
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container fluid>
            <v-row justify="space-between" v-if="hasContributions">
              <v-col xs="12" sm="6" md="4" lg="3" class="required">
                {{ $t("components.formulas.contribution") }}
              </v-col>
              <v-col
                cols="auto"
                v-if="
                  formulas.filter((o) => o.SystemName === 'Contribution')
                    .length === 0
                "
              >
                <v-btn
                  dark
                  color="success"
                  @click="addPlanFormula('Contribution', true)"
                >
                  <v-icon>add</v-icon>
                  {{ $t("globals.add") }}
                </v-btn>
              </v-col>
              <v-col cols="auto" v-else>
                <v-row no-gutters>
                  <v-col cols="auto" style="margin-right: 10px">
                    <v-btn
                      dark
                      color="secondary"
                      @click="
                        viewFormula(
                          formulas.filter(
                            (o) => o.SystemName === 'Contribution'
                          )[0]
                        )
                      "
                    >
                      <v-icon>remove_red_eye</v-icon>
                      {{ $t("globals.view") }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto" style="margin-right: 10px">
                    <v-btn
                      dark
                      color="primary"
                      @click="editPlanFormula('Contribution', true)"
                    >
                      <v-icon>edit</v-icon>
                      {{ $t("globals.edit") }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      color="error"
                      dark
                      @click="deleteFormula('Contribution')"
                    >
                      <v-icon>delete</v-icon>
                      {{ $t("globals.remove") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "Contribution",
                })
              }}
            </v-row>
            <template v-if="adminFeesPerTerm">
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'AdminFee' && o.CoverageIdentityId===planIdentityId)
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('AdminFee')"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'AdminFee' && o.CoverageIdentityId===planIdentityId
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('AdminFee')"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('AdminFee')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PPTAdminTax' && o.CoverageIdentityId===planIdentityId)
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('PPTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PPTAdminTax' && o.CoverageIdentityId===planIdentityId
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PPTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PPTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'RSTAdminTax' && o.CoverageIdentityId===planIdentityId)
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('RSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'RSTAdminTax' && o.CoverageIdentityId===planIdentityId
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('RSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('RSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'HSTAdminTax' && o.CoverageIdentityId===planIdentityId)
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('HSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'HSTAdminTax' && o.CoverageIdentityId===planIdentityId
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('HSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('HSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PSTAdminTax' && o.CoverageIdentityId===planIdentityId)
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('PSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PSTAdminTax' && o.CoverageIdentityId===planIdentityId
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'GSTAdminTax' && o.CoverageIdentityId===planIdentityId)
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('GSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'GSTAdminTax' && o.CoverageIdentityId===planIdentityId
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('GSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('GSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "AdminFee",
                })
              }}
              {{
                clearFormula({
                  systemName: "PPTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "RSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "HSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "GSTAdminTax",
                })
              }}
            </v-row>
            <template v-if="hasFlatAdminFees">
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.flat_admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatAdminFee')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatAdminFee')"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatAdminFee'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatAdminFee')"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatAdminFee')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.flat_admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatPPTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatPPTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatPPTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatPPTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatPPTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.flat_admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatRSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatRSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatRSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatRSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatRSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.flat_admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatHSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatHSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatHSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatHSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatHSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.flat_admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatPSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatPSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatPSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatPSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatPSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.flat_admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatGSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatGSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatGSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatGSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatGSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.flat_admin_fees_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanFlatQSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanFlatQSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanFlatQSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanFlatQSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanFlatQSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "PlanFlatAdminFee",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanFlatPPTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanFlatRSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanFlatHSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanFlatPSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanFlatGSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanFlatQSTAdminTax",
                })
              }}
            </v-row>
            <template v-if="hasClaimAdminFees">
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.claim_admin_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimAdminFee')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimAdminFee')"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimAdminFee'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimAdminFee')"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimAdminFee')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.claim_admin_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimPPTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimPPTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimPPTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimPPTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimPPTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.claim_admin_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimRSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimRSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimRSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimRSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimRSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.claim_admin_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimHSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimHSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimHSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimHSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimHSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.claim_admin_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimPSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimPSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimPSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimPSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimPSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.claim_admin_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimGSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimGSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimGSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimGSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimGSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.claim_admin_fees_qst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PlanClaimQSTAdminTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="rgb(100, 186, 95)"
                    @click="addPlanFormula('PlanClaimQSTAdminTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="rgb(0, 168, 215)"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PlanClaimQSTAdminTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PlanClaimQSTAdminTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PlanClaimQSTAdminTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "PlanClaimAdminFee",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanClaimPPTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanClaimRSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanClaimHSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanClaimPSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanClaimGSTAdminTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PlanClaimQSTAdminTax",
                })
              }}
            </v-row>
            <template v-if="operatingFeesPerTerm">
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3" class="required">
                  {{ $t("components.formulas.operating_fee") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'OperatingFee')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('OperatingFee')"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'OperatingFee'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('OperatingFee')"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('OperatingFee')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.operating_fees_ppt") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PPTOperatingTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('PPTOperatingTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PPTOperatingTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PPTOperatingTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PPTOperatingTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.operating_fees_rst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'RSTOperatingTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('RSTOperatingTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'RSTOperatingTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('RSTOperatingTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('RSTOperatingTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.operating_fees_hst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'HSTOperatingTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('HSTOperatingTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'HSTOperatingTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('HSTOperatingTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('HSTOperatingTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.operating_fees_pst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'PSTOperatingTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('PSTOperatingTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'PSTOperatingTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('PSTOperatingTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('PSTOperatingTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col xs="12" sm="6" md="4" lg="3">
                  {{ $t("components.formulas.operating_fees_gst") }}
                </v-col>
                <v-col
                  cols="auto"
                  v-if="
                    formulas.filter((o) => o.SystemName === 'GSTOperatingTax')
                      .length === 0
                  "
                >
                  <v-btn
                    dark
                    color="success"
                    @click="addPlanFormula('GSTOperatingTax', true)"
                  >
                    <v-icon>add</v-icon>
                    {{ $t("globals.add") }}
                  </v-btn>
                </v-col>
                <v-col cols="auto" v-else>
                  <v-row no-gutters>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="secondary"
                        @click="
                          viewFormula(
                            formulas.filter(
                              (o) => o.SystemName === 'GSTOperatingTax'
                            )[0]
                          )
                        "
                      >
                        <v-icon>remove_red_eye</v-icon>
                        {{ $t("globals.view") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto" style="margin-right: 10px">
                      <v-btn
                        dark
                        color="primary"
                        @click="editPlanFormula('GSTOperatingTax', true)"
                      >
                        <v-icon>edit</v-icon>
                        {{ $t("globals.edit") }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        dark
                        @click="deleteFormula('GSTOperatingTax')"
                      >
                        <v-icon>delete</v-icon>
                        {{ $t("globals.remove") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "OperatingFee",
                })
              }}
              {{
                clearFormula({
                  systemName: "PPTOperatingTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "RSTOperatingTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "HSTOperatingTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "PSTOperatingTax",
                })
              }}
              {{
                clearFormula({
                  systemName: "GSTOperatingTax",
                })
              }}
            </v-row>
            <v-row
              justify="space-between"
              v-if="hasEmployerContributionsPerTerm"
            >
              <v-col xs="12" sm="6" md="4" lg="3" class="required">
                {{ $t("components.formulas.employer_contribution") }}
              </v-col>
              <v-col
                cols="auto"
                v-if="
                  formulas.filter(
                    (o) => o.SystemName === 'EmployerContribution'
                  ).length === 0
                "
              >
                <v-btn
                  dark
                  color="success"
                  @click="addPlanFormula('EmployerContribution')"
                >
                  <v-icon>add</v-icon>
                  {{ $t("globals.add") }}
                </v-btn>
              </v-col>
              <v-col cols="auto" v-else>
                <v-row no-gutters>
                  <v-col cols="auto" style="margin-right: 10px">
                    <v-btn
                      dark
                      color="secondary"
                      @click="
                        viewFormula(
                          formulas.filter(
                            (o) => o.SystemName === 'EmployerContribution'
                          )[0]
                        )
                      "
                    >
                      <v-icon>remove_red_eye</v-icon>
                      {{ $t("globals.view") }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto" style="margin-right: 10px">
                    <v-btn
                      dark
                      color="primary"
                      @click="editPlanFormula('EmployerContribution')"
                    >
                      <v-icon>edit</v-icon>
                      {{ $t("globals.edit") }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      color="error"
                      dark
                      @click="deleteFormula('EmployerContribution')"
                    >
                      <v-icon>delete</v-icon>
                      {{ $t("globals.remove") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row v-else>
              {{
                clearFormula({
                  systemName: "EmployerContribution",
                })
              }}
            </v-row>
            
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-row v-else>
        {{
          clearFormula({
            systemName: "Contribution",
          })
        }}
        {{
          clearFormula({
            systemName: "AdminFee",
          })
        }}
        {{
          clearFormula({
            systemName: "PPTAdminTax",
          })
        }}
        {{
          clearFormula({
            systemName: "RSTAdminTax",
          })
        }}
        {{
          clearFormula({
            systemName: "HSTAdminTax",
          })
        }}
        {{
          clearFormula({
            systemName: "PSTAdminTax",
          })
        }}
        {{
          clearFormula({
            systemName: "GSTAdminTax",
          })
        }}
        {{
          clearFormula({
            systemName: "OperatingFee",
          })
        }}
        {{
          clearFormula({
            systemName: "PPTOperatingTax",
          })
        }}
        {{
          clearFormula({
            systemName: "RSTOperatingTax",
          })
        }}
        {{
          clearFormula({
            systemName: "HSTOperatingTax",
          })
        }}
        {{
          clearFormula({
            systemName: "PSTOperatingTax",
          })
        }}
        {{
          clearFormula({
            systemName: "GSTOperatingTax",
          })
        }}
        {{
          clearFormula({
            systemName: "EmployerContribution",
          })
        }}
      </v-row>
    </v-expansion-panels>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import ErrorHandler from "@/components/ErrorHandler";
import { translate2 } from "@/trans2";
import FormulaBuilder from "@/components/PlanBuilder/FormulaBuilder";
export default {
  name: "formulas",
  components: {
    FormulaBuilder,
    ErrorHandler,
  },
  props: [
    "formulas",
    "coverage",
    "hasAdminFees",
    "hasFlatAdminFees",
    "hasClaimAdminFees",
    "hasOperatingFees",
    "hasContributions",
    "adminFeesPerTerm",
    "operatingFeesPerTerm",
    "planIdentityId",
    "parameters",
    "fields",
    "trackHours",
    "hasStores",
    "disabled",
    "hasEmployerContributionsPerTerm",
    "taxParameters",
    "planVariables",
    "claimParameters"
  ],
  data() {
    return {
      error: null,
      params: [],
      formula: null,
      isNew: true,
      dialogs: {
        builder: false,
        delete: false,
        view: false,
      },
      coverageItem: null,
      valid: false,
      tested: false,
    };
  },
  computed: {
    formTitle() {
      return this.isNew
        ? this.$t("components.formulas.create_new_formula")
        : this.$t("components.formulas.edit_formula_{0}", [
            this.$t("enums." + this.formula.SystemName),
          ]);
    },
    formIcon() {
      return this.isNew ? "add" : "edit";
    },
    formColor() {
      return this.isNew ? this.$vuetify.theme.themes.light.success : this.$vuetify.theme.themes.light.primary;
    },
  },
  methods: {
    rstRowClass(coverageItem) {
      if (coverageItem.HasVolume) {
        if (coverageItem.IncludePPT) {
          return "coverage-row-odd";
        }
        return "coverage-row-even";
      }
      if (coverageItem.IncludePPT) {
        return "coverage-row-even";
      }
      return "coverage-row-odd";
    },
    adminFeesRowClass(coverageItem) {
      if (coverageItem.HasVolume) {
        if (coverageItem.IncludePPT) {
          if (coverageItem.IncludeRST) {
            return "coverage-row-even";
          }
          return "coverage-row-odd";
        }
        if (coverageItem.IncludeRST) {
          return "coverage-row-odd";
        }
        return "coverage-row-even";
      }
      if (coverageItem.IncludePPT) {
        if (coverageItem.IncludeRST) {
          return "coverage-row-odd";
        }
        return "coverage-row-even";
      }
      if (coverageItem.IncludeRST) {
        return "coverage-row-even";
      }
      return "coverage-row-odd";
    },
    hstRowClass(coverageItem) {
      if (this.adminFeesRowClass(coverageItem).indexOf("odd") > -1) {
        return "coverage-row-even";
      } else {
        return "coverage-row-odd";
      }
    },
    gstRowClass(coverageItem) {
      if (this.adminFeesRowClass(coverageItem).indexOf("odd") > -1) {
        return "coverage-row-odd";
      } else {
        return "coverage-row-even";
      }
    },
    pstRowClass(coverageItem) {
      if (this.adminFeesRowClass(coverageItem).indexOf("odd") > -1) {
        return "coverage-row-even";
      } else {
        return "coverage-row-odd";
      }
    },
    taxableBenefitProvRowClass(coverageItem) {
      if (coverageItem.HasVolume) {
        if (
          (coverageItem.IncludePPT && !coverageItem.IncludeRST) ||
          (!coverageItem.IncludePPT && coverageItem.IncludeRST)
        ) {
          if (this.hasAdminFees && coverageItem.HasAdminFees) {
            if (coverageItem.IncludeHST) {
              return "coverage-row-odd";
            }
            return "coverage-row-even";
          }
          return "coverage-row-odd";
        }
        if (this.hasAdminFees && coverageItem.HasAdminFees) {
          if (coverageItem.IncludeHST) {
            return "coverage-row-even";
          }
          return "coverage-row-odd";
        }
        return "coverage-row-even";
      }
      if (
        (coverageItem.IncludePPT && !coverageItem.IncludeRST) ||
        (!coverageItem.IncludePPT && coverageItem.IncludeRST)
      ) {
        if (this.hasAdminFees && coverageItem.HasAdminFees) {
          if (coverageItem.IncludeHST) {
            return "coverage-row-even";
          }
          return "coverage-row-odd";
        }
        return "coverage-row-even";
      }
      if (this.hasAdminFees && coverageItem.HasAdminFees) {
        if (coverageItem.IncludeHST) {
          return "coverage-row-odd";
        }
        return "coverage-row-even";
      }
      return "coverage-row-odd";
    },
    taxableBenefitFedRowClass(coverageItem) {
      if (this.taxableBenefitProvRowClass(coverageItem).indexOf("odd") > -1) {
        return "coverage-row-even";
      } else {
        return "coverage-row-odd";
      }
    },
    viewFormula(formula) {
      this.formula = formula;
      this.dialogs.view = true;
    },
    trans(item, field) {
      return translate2(item, field);
    },
    addCoverageFormula(systemName, coverage, filter, tax) {
      this.formula = {
        SystemName: systemName,
        PlanIdentityId: this.planIdentityId,
        Formula: "",
        CoverageIdentity: coverage,
      };
      let params1 = [];
      if (!filter) {
        params1 = this.parameters;
      } else {
        for (let param of this.parameters) {
          if (
            param.ConfigurationParameter.CoverageIdentity !== null &&
            param.ConfigurationParameter.CoverageIdentity !== undefined &&
            param.ConfigurationParameter.CoverageIdentity.Identifier ===
              coverage.Identifier
          ) {
            for (let f of filter.filters) {
              if (param.ConfigurationParameter.UsedInFormulaType === f) {
                if (filter.negation) {
                  break;
                } else {
                  params1.push(param);
                  break;
                }
              }
            }
          }
        }
      }
      if (tax) {
        const params2 = this.taxParameters;
        this.params = params1.concat(params2);
      } else {
        this.params = params1;
      }
      const params3 = this.claimParameters;
      if (systemName.toLowerCase().indexOf("claim") || systemName.toLowerCase().indexOf("adminfee") > -1) {
        this.params = this.params.concat(params3);
      }
      
      this.coverageItem = coverage;
      this.isNew = true;
      this.dialogs.builder = true;
    },
    async editCoverageFormula(systemName, coverage, filter, tax) {
      this.formula = this.formulas.filter(
        (o) =>
          o.SystemName === systemName &&
          o.CoverageIdentity &&
          o.CoverageIdentity.Identifier === coverage.Identifier
      )[0];
      try {
        let params1 = [];
        console.log("FFFFFFFFFFFFFF", filter);
        if (!filter) {
          params1 = this.parameters;
        } else {
          for (let param of this.parameters) {
            if (
              param.ConfigurationParameter.CoverageIdentity !== null &&
              param.ConfigurationParameter.CoverageIdentity !== undefined &&
              param.ConfigurationParameter.CoverageIdentity.Identifier ===
                coverage.Identifier
            ) {
              for (let f of filter.filters) {
                if (param.ConfigurationParameter.UsedInFormulaType === f) {
                  if (filter.negation) {
                    break;
                  } else {
                    params1.push(param);
                    break;
                  }
                }
              }
            }
          }
        }
        if (tax) {
          const params2 = this.taxParameters;
          this.params = params1.concat(params2);
        } else {
          this.params = params1;
        }

        const params3 = this.claimParameters;
        if (systemName.toLowerCase().indexOf("claim") || systemName.toLowerCase().indexOf("adminfee") > -1) {
          this.params = this.params.concat(params3);
        }

        this.coverageItem = coverage;
        this.isNew = false;
        this.dialogs.builder = true;
      } catch (error) {
        this.error = error;
      }
    },
    async addPlanFormula(systemName, tax) {
      this.formula = {
        SystemName: systemName,
        PlanIdentityId: this.planIdentityId,
        CoverageIdentityId: this.planIdentityId,
        Formula: "",
      };
      try {
        const params1 = this.parameters;
        if (tax) {
          const params2 = this.taxParameters;
          this.params = params1.concat(params2);
        } else {
          this.params = params1;
        }
        const params3 = this.claimParameters;
        if (systemName.toLowerCase().indexOf("claim") || systemName.toLowerCase().indexOf("adminfee") > -1) {
          this.params = this.params.concat(params3);
        }
        this.coverageItem = null;
        this.isNew = true;
        this.dialogs.builder = true;
      } catch (error) {
        this.error = error;
      }
    },
    async editPlanFormula(systemName, tax) {
      console.log(systemName, this.formulas);
      this.formula = this.formulas.filter(
        (o) => o.SystemName === systemName && o.CoverageIdentityId===this.planIdentityId
      )[0];
      try {
        const params1 = this.parameters;
        if (tax) {
          const params2 = this.taxParameters;
          this.params = params1.concat(params2);
        } else {
          this.params = params1;
        }
        const params3 = this.claimParameters;
        if (systemName.toLowerCase().indexOf("claim") || systemName.toLowerCase().indexOf("adminfee") > -1) {
          this.params = this.params.concat(params3);
        }
        this.coverageItem = null;
        this.isNew = false;
        this.dialogs.builder = true;
      } catch (error) {
        this.error = error;
      }
    },
    formulaValid() {
      if (!this.$refs.builder) return false;
      return this.$refs.builder.tested;
    },
    close() {
      this.formula = null;
      this.dialogs.builder = false;
      this.dialogs.delete = false;
      this.dialogs.view = false;
    },
    save() {
      let formula = [];
      if (this.coverageItem) {
        formula = this.formulas.filter(
          (o) =>
            o.SystemName === this.formula.SystemName &&  o.CoverageIdentity &&
            o.CoverageIdentity.Identifier === this.coverageItem.Identifier
        );
      } else {
        formula = this.formulas.filter(
          (o) => o.SystemName === this.formula.SystemName  && o.CoverageIdentityId===this.planIdentityId
        );
      }
      console.log(this.formula, this.planIdentityId, this.formulas, formula)
      if (formula.length > 0) {
        this.formula.updated = true;
        this.formulas[this.formulas.indexOf(formula[0])] = JSON.parse(
          JSON.stringify(this.formula)
        );
      } else {
        this.formula.added = true;
        this.formulas.push(JSON.parse(JSON.stringify(this.formula)));
      }
      this.$emit("update", this.formulas);
      this.close();
    },
    deleteCoverageFormula(systemName, coverageItem) {
      this.formula = this.formulas.filter(
        (o) =>
          o.SystemName === systemName &&
          o.CoverageIdentity !== null &&
          o.CoverageIdentity !== undefined &&
          o.CoverageIdentity.Identifier === coverageItem.Identifier
      )[0];
      this.dialogs.delete = true;
    },
    deleteFormula(systemName) {
      console.log(systemName, this.formulas);
      this.formula = this.formulas.filter(
        (o) => o.SystemName === systemName && o.CoverageIdentityId===this.planIdentityId
      )[0];
      this.dialogs.delete = true;
    },
    tryDelete() {
      let formula;
      formula = this.formulas.filter(
        (o) =>
          o.SystemName === this.formula.SystemName &&
          o.CoverageIdentity !== null &&
          o.CoverageIdentity !== undefined &&
          o.CoverageIdentity.Identifier ===
            this.formula.CoverageIdentity.Identifier
      );
      this.formulas.splice([this.formulas.indexOf(formula[0])], 1);
      this.$emit("update", this.formulas);
      this.close();
    },
    clearFormula({ systemName, coverageIdentifier }) {
      let formula;
      formula = this.formulas.filter(
        (o) =>
          o.SystemName === systemName &&
          o.CoverageIdentity !== null &&
          o.CoverageIdentity !== undefined &&
          o.CoverageIdentity.Identifier === coverageIdentifier
      );
      if (formula.length > 0) {
        this.formulas.splice([this.formulas.indexOf(formula[0])], 1);
        // console.log("$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$", this.formulas);
        this.$emit("update", this.formulas);
      }
    },
  },
};
</script>

<style scoped>
.required:before {
  content: "* ";
  color: red;
  font-weight: 900;
}
.formula-group-header-0 {
  background-color: #36454F;
  color: white;
}
.formula-group-header-1 {
  background-color: #E30B5C;
  color: white;
}
.formula-group-header-2 {
  background-color: #36454F;
  color: white;
}
.formula-group-header-3 {
  background-color: #E30B5C;
  color: white;
}
.formula-group-header-0 .v-icon {
  color: white;
}
.formula-group-header-0 .v-expansion-panel-header__icon .v-icon {
  color: white !important;
}
.formula-group-header-1 .v-icon {
  color: white;
}
.formula-group-header-1 .v-expansion-panel-header__icon .v-icon {
  color: white !important;
}
.formula-group-header-2 .v-icon {
  color: white;
}
.formula-group-header-2 .v-expansion-panel-header__icon .v-icon {
  color: white !important;
}
.formula-group-header-3 .v-icon {
  color: white;
}
.formula-group-header-3 .v-expansion-panel-header__icon .v-icon {
  color: white !important;
}
.coverage-row-odd {
  background-color: rgb(255, 255, 255);
}
.coverage-row-odd {
  background-color: rgba(0, 0, 0, 0.03);
}
.v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
